import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../supabaseClient'
import Sidebar from './Sidebar'
import MainLayout from './MainLayout'
import { useUser } from '../contexts/UserContext'
import usePresence from '../hooks/usePresence'

// Layout wrapper untuk halaman yang butuh Sidebar & Navbar
export default function ProtectedLayout({ children }) {
  const navigate = useNavigate()
  const [isChecking, setIsChecking] = useState(true)
  const [isAuthorized, setIsAuthorized] = useState(false)
  const [isOnline, setIsOnline] = useState(navigator.onLine)
  const { userInfo } = useUser()

  // Track online presence (heartbeat)
  usePresence(userInfo?.id)

  // Monitor network status
  useEffect(() => {
    const handleOnline = () => setIsOnline(true)
    const handleOffline = () => setIsOnline(false)
    
    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)
    
    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [])

  useEffect(() => {
    let timeoutId = null
    let isMounted = true
    
    const checkUserStatus = async () => {
      try {
        // Set timeout untuk mencegah stuck loading
        timeoutId = setTimeout(() => {
          if (isMounted) {
            console.warn('Authorization check timeout')
            if (timeoutId) clearTimeout(timeoutId)
            setIsChecking(false)
            navigate('/login', { 
              state: { 
                message: 'Timeout saat memeriksa authorization. Silakan login kembali.' 
              }
            })
          }
        }, 10000) // 10 detik timeout

        // Get current user with retry logic
        let user = null
        let authError = null
        let retries = 3
        
        for (let i = 0; i < retries; i++) {
          if (!isMounted) {
            clearTimeout(timeoutId)
            return
          }
          
          try {
            const { data, error } = await supabase.auth.getUser()
            user = data?.user
            authError = error
            
            if (!error || error.message?.includes('Invalid Refresh Token')) {
              break // Success atau refresh token invalid (butuh re-login)
            }
            
            // Retry jika network error
            if (i < retries - 1) {
              await new Promise(resolve => setTimeout(resolve, 1000 * (i + 1))) // Exponential backoff
            }
          } catch (err) {
            console.error(`Attempt ${i + 1} failed:`, err)
            authError = err
          }
        }
        
        if (!isMounted) {
          clearTimeout(timeoutId)
          return
        }
        
        if (authError) {
          console.error('Auth error after retries:', authError)
          if (timeoutId) clearTimeout(timeoutId)
          
          // Jika invalid refresh token, clear session dan redirect
          if (authError.message?.includes('Invalid Refresh Token') || 
              authError.message?.includes('refresh_token_not_found')) {
            await supabase.auth.signOut()
            if (isMounted) {
              navigate('/login', { 
                state: { 
                  message: 'Sesi Anda telah berakhir. Silakan login kembali.' 
                }
              })
            }
            return
          }
          
          // Untuk error lain (network, server), coba gunakan session dari localStorage
          const localSession = localStorage.getItem('supabase.auth.token')
          if (localSession) {
            if (isMounted) {
              setIsAuthorized(true)
              setIsChecking(false)
            }
            return
          }
          
          if (isMounted) {
            navigate('/login', { 
              state: { 
                message: 'Tidak dapat terhubung ke server. Silakan coba lagi.' 
              }
            })
          }
          return
        }

        if (!user) {
          if (timeoutId) clearTimeout(timeoutId)
          if (isMounted) {
            navigate('/login')
          }
          return
        }

        // Check user approval status
        const { data: userData, error } = await supabase
          .from('users')
          .select('status, rejection_reason')
          .eq('id', user.id)
          .single()

        if (!isMounted) {
          clearTimeout(timeoutId)
          return
        }

        // Clear timeout jika berhasil
        if (timeoutId) clearTimeout(timeoutId)

        if (error) {
          console.error('Database error:', error)
          // Jika error karena RLS atau permission, tetap izinkan akses
          // Ini untuk backward compatibility dengan user lama yang mungkin belum ada status
          if (isMounted) {
            setIsAuthorized(true)
            setIsChecking(false)
          }
          return
        }

        if (!userData) {
          await supabase.auth.signOut()
          if (isMounted) {
            navigate('/login', { 
              state: { 
                message: 'Data user tidak ditemukan. Silakan login kembali.' 
              }
            })
          }
          return
        }

        // Block if pending
        if (userData.status === 'pending') {
          await supabase.auth.signOut()
          if (isMounted) {
            navigate('/login', { 
              state: { 
                message: '⏳ Akun Anda masih menunggu persetujuan admin. Silakan hubungi administrator untuk informasi lebih lanjut.' 
              }
            })
          }
          return
        }

        // Block if rejected
        if (userData.status === 'rejected') {
          await supabase.auth.signOut()
          const reason = userData.rejection_reason ? ` Alasan: ${userData.rejection_reason}` : ''
          if (isMounted) {
            navigate('/login', { 
              state: { 
                message: `❌ Akun Anda ditolak oleh admin.${reason}` 
              }
            })
          }
          return
        }

        // Status approved - allow access
        if (isMounted) {
          setIsAuthorized(true)
          setIsChecking(false)
        }

      } catch (error) {
        console.error('Error checking user status:', error)
        if (timeoutId) clearTimeout(timeoutId)
        // Jika error tidak terduga, tetap izinkan akses untuk backward compatibility
        if (isMounted) {
          setIsAuthorized(true)
          setIsChecking(false)
        }
      }
    }

    checkUserStatus()

    // Cleanup timeout on unmount
    return () => {
      isMounted = false
      if (timeoutId) {
        clearTimeout(timeoutId)
        timeoutId = null
      }
    }
  }, [navigate])

  // Show offline message
  if (!isOnline) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-red-50 via-orange-50 to-yellow-50">
        <div className="text-center p-8 bg-white rounded-2xl shadow-xl max-w-md">
          <div className="text-6xl mb-4">📡</div>
          <h1 className="text-2xl font-bold text-gray-800 mb-2">No Internet Connection</h1>
          <p className="text-gray-600 mb-6">
            Please check your internet connection and try again.
          </p>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-3 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-lg font-semibold hover:from-orange-600 hover:to-red-600 transition-all"
          >
            Try Again
          </button>
        </div>
      </div>
    )
  }

  // Show loading while checking
  if (isChecking) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-blue-50 via-blue-50 to-blue-50">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
          <p className="text-gray-600 font-medium">Checking authorization...</p>
        </div>
      </div>
    )
  }

  // Only render if authorized
  if (!isAuthorized) {
    return null
  }

  return (
    <>
      <Sidebar />
      <MainLayout>
        <div className="p-4 sm:p-6 lg:p-8">
          {children}
        </div>
      </MainLayout>
    </>
  )
}
